import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import { clefNames } from 'shared/definitions/musicTheory/clefs';
import type { InteractiveOptions, LayoutSettings, LayoutItem } from './interactivePrototype';
import InteractivePrototype from './interactivePrototype';
import utils from '../utils';
import * as Skills from '../uTheory.Skills/skills';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import SkillCalculator from '../uTheory.Skills/skillCalculator';
import RomanNumeral from '../uTheory.Notation/romanNumeral';
import Staff from '../uTheory.Notation/staff';
import Voice from '../uTheory.Notation/voice';
import NoteHolder from '../uTheory.Notation/noteHolder';
import Clef from '../uTheory.Notation/clef';
import KeySignature from '../uTheory.Notation/keySignature';
import Music from '../uTheory.Notation/music';
import Editable from '../uTheory.Notation/editable';
import Measure from '../uTheory.Notation/measure';
import Chord from '../uTheory.Notation/chord';
import CustomKeySignature from '../uTheory.Notation/customKeySignature';
import KeySigPlayer from './keySigPlayer';
import type { Note } from '../notation';

interface RomanNumeralDrawOptions extends InteractiveOptions {
  spacing?: 'close' | 'open';
  /** Only available in close spacing!!! */
  drawKey?: boolean;
}

interface RomanNumeralDrawQuestion {
  rn: RomanNumeral;
  clef: ClefName;
  skills: string[];
  text: string[];
  /** accidental font formatted */
  rnText: string;
  /** accidental font formatted */
  keyText: string;
}

interface RomanNumeralDrawLayout extends LayoutSettings {
  romanInstructions: LayoutItem;
}

type RomanNumeralDrawManualQuestion = string | [string, { removeMixture?: boolean }];

/**
 * Interactive for drawing roman numerals. "Draw G Maj: ii65"
 */
export default class RomanNumeralDraw extends InteractivePrototype {
  declare options: RomanNumeralDrawOptions;

  skillsCalculator!: SkillCalculator;

  staves!: {
    staff: Staff;
    voice: Voice;
    noteHolder: NoteHolder & Pick<Required<NoteHolder>, 'editable'>;
    clef: Clef;
  }[];

  qg!: QuestionGenerator;

  declare questions: RomanNumeralDrawQuestion[];

  declare currentQuestion: RomanNumeralDrawQuestion;

  mode: 'keySignature' | 'romanNumeral' | undefined;

  customKeySig!: CustomKeySignature;

  /** prevent double submit */
  checklock: boolean;

  keySigPlayer!: KeySigPlayer;

  private romanInstructionsDiv!: HTMLDivElement;

  declare layout: RomanNumeralDrawLayout;

  /**
   * constructor
   * @param displayDiv - div in which to render
   * @param options - see InteractivePrototype, where questions: string[] "Bb M: ii42/bass" // omit clef in open spacing.
   */
  constructor(displayDiv: HTMLDivElement, options: RomanNumeralDrawOptions = {}) {
    if (options.drawKey && options.spacing === 'open') {
      throw new Error('Roman Numeral Draw Key is only available in close spacing.');
    }

    if (options.drawKey && options.testMode) {
      throw new Error('Roman Numeral Draw Key is not yet available in test mode.');
    }
    options.skills = options.skills || [];
    const skills = options.skills!.map(Skills.makeString);

    options.spacing =
      options.spacing || (skills.find((s) => s === 'rn_open_spacing') ? 'open' : 'close');

    super(displayDiv, options);
    if (options.drawKey) this.mode = 'keySignature';
    this.targetSpeedFromSkills = RomanNumeralDraw.registry[0]!.skills;
    this.tempo = 180;
    this.checklock = false;

    this.layout = {
      instructions: { percent: 15, noWrap: true },
      romanInstructions: { percent: 15, visible: true },
    };

    this.init(displayDiv, options);
  }

  /**
   * setup settings for elements to displayDiv
   * @param displayDiv - div to render to
   * @param options - interactive options
   */
  init(displayDiv: HTMLDivElement, options: RomanNumeralDrawOptions) {
    this.settings = {
      instructions: true,
      editor: {
        fixedWidth: options.drawKey ? 200 : 150,
        ariaLive: 'polite',
        ariaTitle: {
          rhythm: false,
          barline: false,
        },
      },
      notation: false,
      letters: {
        checkButton: true,
        accidentalButtons: { noDoubleTap: true },
        trashButton: true,
      },
      checkMark: {},
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    this.skillsCalculator = new SkillCalculator();
    return super.init(displayDiv, options);
  }

  displayInstructions() {
    this.questionPrompt = this.mode === 'keySignature' ? 'Draw the key' : 'Draw the roman numeral';
    this.instructionsDiv!.style.height = 'unset';
    super.displayInstructions();
    this.romanInstructionsDiv.innerHTML =
      this.mode === 'keySignature'
        ? this.currentQuestion.keyText
        : `${this.currentQuestion.keyText}: ${this.currentQuestion.rnText}`;
    this.sizeTextDiv(0, this.romanInstructionsDiv, {
      noWrap: true,
      maintainCurrentHeight: true,
    });
  }

  createCustomDivs() {
    this.createDiv('romanInstructionsDiv', { textAlign: 'center' });
    this.romanInstructionsDiv.setAttribute('role', 'alert');
    this.displayDiv.insertBefore(this.romanInstructionsDiv, this.lettersDiv!);
    this.layout.romanInstructions.div = this.romanInstructionsDiv;
    this.layout.romanInstructions.callback = (height: number) =>
      // console.log('sizing roman div to', height);
      this.sizeTextDiv(height, this.romanInstructionsDiv, {
        noWrap: true,
      });
  }

  /**
   * initialize the this.music `Music` object
   */
  initializeMusic() {
    this.keySigPlayer = new KeySigPlayer();
    const keySig = new KeySignature('C', 'Major');
    this.staves =
      this.options.spacing === 'open'
        ? (['treble', 'bass'] as ClefName[]).map(makeStaff, this)
        : (['treble'] as ClefName[]).map(makeStaff, this);

    const musicElements = this.staves.reduce(
      (arr: (Staff | KeySignature | Voice)[], staff) =>
        arr.concat([keySig, staff.staff, staff.voice]),
      []
    );

    if (this.staves.length === 2) {
      const editable1 = this.staves[0]!.noteHolder.editable;
      const editable2 = this.staves[1]!.noteHolder.editable;
      editable1!.link(editable2);
    }

    this.music = new Music(musicElements);

    function makeStaff(this: RomanNumeralDraw, clefName: ClefName) {
      const clef = new Clef(clefName);
      const staff = new Staff(clef);
      const nh = new NoteHolder('', 'w');
      nh.editable = new Editable(nh, {
        pitch: true,
        accidental: true,
        deleteable: ['ghost'],
        selectable: true,
        maxNotes: 6,
        keySignature: this.options.drawKey ? this.customKeySig : undefined,
      });
      const measure = new Measure(nh);
      const voice = new Voice(measure);
      voice.setStaff(staff);
      return {
        staff,
        voice,
        noteHolder: nh as NoteHolder & Pick<Required<NoteHolder>, 'editable'>,
        clef,
      };
    }
  }

  /**
   * initialize the this.editor `Editor` object
   */
  initializeEditor() {
    super.initializeEditor();

    this.customKeySig = new CustomKeySignature({ editor: this.editor!, accidental: '#' });

    this.editor!.addListener('notePitchChange', uncolor, this);
    this.editor!.addListener('noteAccidentalChange', uncolor, this);
    function uncolor(this: RomanNumeralDraw, note: Note) {
      if (note.color) {
        delete note.color;
        /* jshint validthis:true */
        this.editor!.redraw();
      }
    }
  }

  generateQuestions(questions: false | RomanNumeralDrawManualQuestion[] = false) {
    this.questionPrompt = `Draw the ${this.mode === 'keySignature' ? 'key' : 'roman numeral'}`;
    this.qg = new QuestionGenerator();

    if (this.options.drill) {
      this.questions = this.generateDrillQuestions();
      return;
    }

    if (!questions && !this.options.questions)
      throw new Error('Manual questions must be provided when not in drill or checkpoint mode');
    this.questions = this.processManualQuestions(questions || this.options.questions!);
  }

  generateDrillQuestions(): RomanNumeralDrawQuestion[] {
    const skills = this.options.skills!.map(Skills.makeString);
    const open = this.options.spacing === 'open' || skills.find((skill) => skill.includes('open'));
    const clefStrings: ClefName[] = ['treble', 'bass'];
    return this.qg
      .roman_numerals(this.options.maxQuestions || 8, skills)
      .map((q: { rn: RomanNumeral; skills: string[]; clef?: ClefName }) => {
        if (!open) q.clef = clefStrings[~~(Math.random() * 2)];
        return q;
      })
      .map(this.processQuestion);
  }

  /**
   * @param questions - "G min:V42/bass"
   */
  processManualQuestions(questions: RomanNumeralDrawManualQuestion[]): RomanNumeralDrawQuestion[] {
    return questions
      .map(
        (
          question
        ): Partial<RomanNumeralDrawQuestion> & Pick<Required<RomanNumeralDrawQuestion>, 'rn'> => {
          let questionString: string;
          let options: { removeMixture?: boolean } = {};
          if (utils.isArray(question)) {
            questionString = question[0];
            options = question[1];
          } else {
            questionString = question;
          }
          // console.log(`Processing questionString: ${questionString}, options: ${options}`);

          let clef = clefNames.find((clefName) => questionString.includes(clefName));
          if (clef) {
            questionString = questionString.slice(0, questionString.indexOf(clef) - 1);
          } else {
            const randomClefNames: ClefName[] = ['treble', 'bass'];
            clef = randomClefNames[~~(Math.random() * 2)];
          }
          const rn = new RomanNumeral(questionString);
          if (options.removeMixture) rn.removeMixture();
          const keyIsMajor = rn.key.quality === 'Major';
          const sd = rn.scaleDegree;
          const inversion = rn.inversion;
          const isSeventh = rn.isSeventh;
          const skills = this.qg.getRNSkills({
            sd,
            inversion,
            isSeventh,
            keyIsMajor,
          });
          return { rn, clef, skills };
        }
      )
      .map(this.processQuestion);
  }

  // eslint-disable-next-line class-methods-use-this
  processQuestion(
    question: Partial<RomanNumeralDrawQuestion> & Pick<Required<RomanNumeralDrawQuestion>, 'rn'>,
    index: number
  ): RomanNumeralDrawQuestion {
    const rnString = question.rn.toString({ font: true });
    question.rnText = rnString;
    question.keyText = question.rn.key.toAccidentalFontString({
      shortQuality: true,
    });
    question.text = [`${index + 1}`];
    return question as RomanNumeralDrawQuestion;
  }

  newQuestion() {
    const priorQuestion = this.questions[this.questionNumber - 1];
    const currentQuestion = this.questions[this.questionNumber];

    const priorQuestionHadSameKey =
      priorQuestion && currentQuestion!.rn.key.equals(priorQuestion.rn.key);
    const priorQuestionHadSameClef = priorQuestion && currentQuestion!.clef === priorQuestion.clef;
    const priorQuestionHadSameKeyAndClef = priorQuestionHadSameClef && priorQuestionHadSameKey;

    if (this.options.drawKey && !priorQuestionHadSameKeyAndClef) this.setMode('keySignature');

    super.newQuestion();
    this.editor!.deselectAll();
    this.staves.forEach((staff) => {
      staff.noteHolder.length = 0;
    });
    if (this.options.spacing !== 'open') {
      this.staves[0]!.staff.clef.setName(currentQuestion!.clef);
      const isCustomKeyable = ['treble', 'bass'].includes(currentQuestion!.clef);
      if (this.options.drawKey && !isCustomKeyable) {
        throw new Error('Only treble and bass clefs can be used for key sig construction.');
      }
    }
    this.editor!.renderer.render();
  }

  newCheckpointQuestion(
    question: RomanNumeralDrawManualQuestion
  ): Promise<RomanNumeralDrawQuestion> {
    return this.loadingPromise.then(() => {
      this.generateQuestions([question]);
      this.newQuestion();
      return Promise.resolve(this.currentQuestion);
    });
  }

  addListeners() {
    const modeSwitch =
      (romanNumeralModeFunction: Function, keySigModeFunction: Function) =>
      (...args: any[]) =>
        this.mode === 'keySignature'
          ? keySigModeFunction(...args)
          : romanNumeralModeFunction(...args);

    const checkAnswerCallback = modeSwitch(
      this.checkAnswer.bind(this),
      this.checkKeySignature.bind(this)
    );
    const accidentalCallback = modeSwitch(
      this.editor!.setAccidental.bind(this.editor),
      this.customKeySig.setAccidentalType.bind(this.customKeySig)
    );
    const deleteCallback = modeSwitch(this.editor!.delete.bind(this.editor), () => {
      this.customKeySig.delete();
    });

    this.letterPanel!.addCheckCallback(checkAnswerCallback);
    this.letterPanel!.addAccidentalCallback(accidentalCallback);
    this.letterPanel!.addCustomButtonCallback(deleteCallback);
  }

  setMode(mode: 'romanNumeral' | 'keySignature') {
    this.mode = mode;
    if (mode === 'romanNumeral') {
      this.customKeySig.lockSignature();
      this.staves.forEach((staff) => {
        staff.noteHolder.editable.options.maxNotes = 6;
      });
      this.editor!.redraw();
      const instructionsHeight = this.instructionsDiv!.clientHeight;
      this.displayInstructions();
      this.instructionsDiv!.style.height = `${instructionsHeight}px`;
      this.sizeTextDiv(0, this.instructionsDiv!, {
        noWrap: true,
        maintainCurrentHeight: true,
      });
      this.sizeTextDiv(0, this.romanInstructionsDiv, {
        noWrap: true,
        maintainCurrentHeight: true,
      });
      return;
    }

    this.customKeySig.unlockSignature();
    this.customKeySig.clear();
    this.staves.forEach((staff) => {
      staff.staff.setCustomKeySignature(this.customKeySig);
      staff.noteHolder.editable.options.maxNotes = 0;
    });
  }

  checkKeySignature() {
    if (this.checklock) return;
    this.checklock = true;
    const clef = this.staves[0]!.clef;
    const keyCheck = this.customKeySig.checkKey(clef);
    const keySignature = this.currentQuestion.rn.key;
    const correct = keyCheck instanceof KeySignature && keySignature.equalsOrRelative(keyCheck);

    const skill = this.skillsCalculator.getKeySignatureSkill(keySignature);

    if (this.optionsTrue('testMode')) {
      this.setMode('romanNumeral');
      return;
    }

    if (correct) {
      this.wrongAnswerCount = 0;
      this.customKeySig.lockSignature();
      this.setMode('romanNumeral');
      this.progressTracker!.rightAnswer(skill);
      this.checkMark!.check(1);
    } else {
      if (this.wrongAnswerCount > 1) {
        const hint = this.customKeySig.hint(keySignature, clef);
        this.editor!.redraw();
        if (typeof hint !== 'undefined') this.drawKeySigHintOnStave(hint, clef);
      }
      this.keySigPlayer.playWrongAnswer();
      this.progressTracker!.wrongAnswer(skill);
      this.wrongAnswerCount++;
      this.checkMark!.x(0.75);
    }
    utils.wait(0.25).then(() => {
      this.checklock = false;
    });
  }

  drawKeySigHintOnStave(hint: string, clef: Clef) {
    const y = clef.getName() === 'treble' ? '70' : '15';
    const svg = this.editor!.renderer.ctx.svg;
    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttributeNS(null, 'fill', 'red');
    text.setAttributeNS(null, 'x', '25');
    text.setAttributeNS(null, 'y', y);
    text.setAttributeNS(null, 'font-size', '8px');

    text.textContent = hint;
    svg.appendChild(text);
  }

  getAnswerObj() {
    // GCR TO-DO: remove first part of check answer logic here, then clean up getAnswerAndData to call this instead of check answer with forceSubmit
    const noteHolder = new NoteHolder(
      this.staves.reduce((arr: Note[], staff) => arr.concat(staff.noteHolder.notes), [])
    );

    const correctChord = this.currentQuestion.rn.chord.equalsIgnoreInversion(noteHolder);
    const correctInversion = this.currentQuestion.rn.chord.equalsIgnoreOctave(noteHolder);

    /*
    console.log(`correctChord: ${correctChord}, correctInversion: ${correctInversion}`);
    console.log('right chord:');
    console.log(this.currentQuestion.rn.chord);
    console.log(`student noteHolder: ${noteHolder}`);
*/
    const correct = correctChord && correctInversion;

    return {
      correct,
      noteHolder,
      correctInversion,
      correctChord,
    };
  }

  checkAnswer(forceSubmit: boolean) {
    if (this.rightAnswerDelay) return;
    if (this.checklock && !forceSubmit) return;
    this.checklock = true;
    utils.wait(0.25).then(() => {
      this.checklock = false;
    });

    const noteHolder = new NoteHolder(
      this.staves.reduce((arr: Note[], staff) => arr.concat(staff.noteHolder.notes), [])
    );

    if (!forceSubmit && noteHolder.length < 3) {
      this.checkMark!.warn('You must draw at least 3 notes to make a chord.');
      return;
    }

    const answerObj = this.getAnswerObj();

    if (this.options.testMode) {
      this.checkpointAnswer();
      return;
    }

    if (answerObj.correct) {
      this.currentQuestion.skills.push(
        noteHolder.isOpenSpacing() ? 'rn_open_spacing' : 'rn_close_spacing'
      );
      this.rightAnswer(1100);
    } else if (this.wrongAnswerCount++) {
      this.wrongAnswer(this.getFeedbackString(answerObj));
      if (this.wrongAnswerCount > 2) {
        this.editorFeedback(answerObj);
      }
    } else {
      this.wrongAnswer();
    }
  }

  /* @param answerObj {
      correct: 0, 0.5, 1,
      noteHolder: student answer noteHolder
    }
  */
  getFeedbackString(answerObj: {
    correct: boolean;
    noteHolder: NoteHolder;
    correctInversion: any;
    correctChord: any;
  }) {
    const noteHolder = answerObj.noteHolder;
    const chord = this.currentQuestion.rn.chord;
    const studentChord = Chord.fromNoteHolder(noteHolder);

    if (!studentChord) {
      return 'These notes do not form any chord.';
    }

    const rootMatch = chord.root.equalsIgnoreOctave(studentChord.root);
    const seventhMatch = chord.isSeventh === studentChord.isSeventh;
    const qualityMatch = chord.quality === studentChord.quality;
    const inversionMatch = answerObj.correctInversion;

    if (!rootMatch) {
      return `You've written ${studentChord.root.aOrAn()} ${studentChord.root.toShortString(
        true
      )} chord instead of ${chord.root.aOrAn()} ${chord.root.toShortString(true)} chord.`;
    }
    if (!seventhMatch) {
      return (
        `You've written a ${studentChord.isSeventh ? '7th chord' : 'triad'} instead ` +
        `of a ${chord.isSeventh ? '7th chord' : 'triad'}.`
      );
    }
    if (!qualityMatch) {
      const qualityString = studentChord.getQualityString(1, true, true);
      const correctQualityString = chord.getQualityString(1, true, true);
      return `You've written ${utils.aOrAn(
        qualityString
      )} ${qualityString} chord, instead of ${utils.aOrAn(
        correctQualityString
      )} ${correctQualityString} chord.`;
    }
    if (!inversionMatch) {
      return `You've written a ${studentChord.getInversionString(
        'full'
      )} chord instead of a ${chord.getInversionString('full')} one.`;
    }

    return '';
  }

  editorFeedback(answerObj: {
    correct: boolean;
    noteHolder: NoteHolder;
    correctInversion: any;
    correctChord: any;
  }) {
    const chord = this.currentQuestion.rn.chord;
    this.editor!.deselectAll();
    answerObj.noteHolder.forEach((note) => {
      if (!chord.noteHolder.noteExistsAtIgnoreOctave(note)) {
        note.color = 'red';
      }
    });
    this.editor!.redraw();
  }

  getAnswerAndData() {
    const answerObj = this.getAnswerObj();
    const noteHolder = answerObj.noteHolder;
    const correct = answerObj.correct;
    const chord = this.currentQuestion.rn.chord;
    const questionText = `Draw ${this.currentQuestion.keyText}: ${this.currentQuestion.rnText}`;

    this.editorFeedback(answerObj);

    return {
      question: questionText,
      questionType: 'RomanNumeralDraw',
      questionText,
      questionSVG: this.editor!.renderer.exportSVG(),
      studentAnswerText: noteHolder.toString(),
      correctAnswerText: chord.noteHolder.toString(),
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.currentQuestion.skills,
    };
  }

  gameOver() {
    this.instructionsDiv!.innerHTML = 'Nice work!';
    this.instructionsDiv!.setAttribute('role', 'alert');
    try {
      this.displayDiv.removeChild(this.lettersDiv!);
    } catch (err) {
      // Swallowing error.
    }
    super.gameOver();
  }

  static registry = [
    {
      description: 'Draw Roman Numerals',
      skills: [
        'triad_rn_major',
        'triad_rn_minor',
        'rn_inversion',
        'seventh_rn_major',
        'seventh_rn_minor',
      ],
      alsoTrains: ['rn_open_spacing', 'rn_close_spacing'],
    },
  ];
}
